import React from 'react';
import { CalendarDays, Download, Edit3, LayoutGrid, ListFilter, Plus, RefreshCw, RotateCcw, Send, Sparkles, Trash2, AlertTriangle, Lock, Unlock, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import StudentLayout from '../../components/layouts/StudentLayout';
import AdminLayout from '../../components/layouts/AdminLayout';
import Button from '../../components/ui/Button';
import ActionToast from '../../components/ui/ActionToast';
import { academicCalendarService } from '../../services/academicCalendarService';
import { hierarchyService } from '../../services/hierarchyService';
import { timetableService } from '../../services/timetableService';
import MyTimetable from '../role-based-dashboard/components/MyTimetable';
import TrainerExceptionsModal from './components/TrainerExceptionsModal';

const DAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const emptySession = { id: '', unit_id: '', room_id: '', trainer_id: '', day_of_week: 1, starts_at: '08:00', ends_at: '09:00' };
const badge = status => ({ draft: 'bg-slate-100 text-slate-700', returned: 'bg-red-100 text-red-700', submitted: 'bg-amber-100 text-amber-800', published: 'bg-emerald-100 text-emerald-800 font-bold' }[status] || 'bg-slate-100 text-slate-700');

const minutesFromTime = time => {
    if (!time) return 0;
    const [h, m] = String(time).split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

const formatTimeFromMinutes = mins => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const isMandatoryRestSlot = (programmeSessions, dayOfWeek, slotStartMins, slotEndMins) => {
    const intervals = programmeSessions
        .filter(row => Number(row?.session?.day_of_week) === Number(dayOfWeek))
        .map(row => ({
            start: minutesFromTime(row?.session?.starts_at),
            end: minutesFromTime(row?.session?.ends_at)
        }))
        .filter(interval => interval.end > interval.start)
        .sort((left, right) => left.start - right.start);

    const blocks = [];
    intervals.forEach(interval => {
        const previous = blocks[blocks.length - 1];
        if (previous && interval.start <= previous.end) previous.end = Math.max(previous.end, interval.end);
        else blocks.push({ ...interval });
    });

    // A rest marker is meaningful only when the class has another lesson
    // later on the same day. Do not show an artificial "rest" after the
    // final class at the end of the day.
    const hasLaterClass = intervals.some(interval => interval.start >= slotEndMins);
    return hasLaterClass && blocks.some(block =>
        block.end === slotStartMins &&
        block.end - block.start >= 360 &&
        slotEndMins - slotStartMins >= 60
    );
};

const violatesTrainerRestRule = (sessionRows, trainerId, dayOfWeek, candidateStart, candidateEnd, excludedIds = new Set()) => {
    if (!trainerId || candidateEnd <= candidateStart) return false;
    const intervals = (sessionRows || [])
        .filter(row => row?.session && String(row.session?.trainer_id || '') === String(trainerId) && Number(row.session?.day_of_week) === Number(dayOfWeek) && !excludedIds.has(row.session?.id))
        .map(row => ({ start: minutesFromTime(row.session?.starts_at), end: minutesFromTime(row.session?.ends_at) }))
        .filter(interval => interval.end > interval.start)
        .concat([{ start: candidateStart, end: candidateEnd }])
        .sort((left, right) => left.start - right.start);
    const blocks = [];
    intervals.forEach(interval => {
        const previous = blocks[blocks.length - 1];
        if (previous && interval.start <= previous.end) previous.end = Math.max(previous.end, interval.end);
        else blocks.push({ ...interval });
    });
    return blocks.some((block, index) => {
        if (block.end - block.start > 360) return true;
        const next = blocks[index + 1];
        return block.end - block.start >= 360 && next && next.start - block.end < 60;
    });
};

const TimetableGridView = ({ sessions, canManage, batch, edit, remove, systemSettings, onSessionMove, onToggleLock }) => {
    const configuredDays = systemSettings?.timetable_working_days || [1, 2, 3, 4, 5];
    const activeDays = [...new Set([...configuredDays, ...sessions.map(row => Number(row?.session?.day_of_week))])]
        .filter(day => day >= 1 && day <= 7)
        .sort((a, b) => a - b);
    const [dragOverCell, setDragOverCell] = React.useState(null);

    // Calculate start & end hours strictly from System Settings timetable_day_starts_at
    const startHour = Math.floor(minutesFromTime(systemSettings?.timetable_day_starts_at || '08:00') / 60);
    const configuredEndHour = Math.ceil(minutesFromTime(systemSettings?.timetable_day_ends_at || '20:00') / 60);
    const endHour = Math.max(startHour + 10, configuredEndHour);

    const totalSlots = endHour - startHour; // Total 1-hour slots per day
    const slotNumbers = Array.from({ length: totalSlots }, (_, i) => i + 1);

    // Group sessions by Programme structure
    const programmeMap = new Map();
    sessions.forEach(row => {
        if (!row?.session || !row?.unit) return;
        const progName = row.unit?.program?.name || row.unit?.program_name || 'Department Programme';
        const level = row.unit?.program_level ?? row.unit?.program?.level ?? '—';
        const module = row.unit?.module_in_level ?? row.unit?.program?.module ?? '—';
        const key = `${progName} (L${level} · M${module})`;

        if (!programmeMap.has(key)) {
            programmeMap.set(key, {
                key,
                name: progName,
                code: row.unit?.program?.code || '',
                level,
                module,
                sessionsList: []
            });
        }
        programmeMap.get(key).sessionsList.push(row);
    });

    const programmeRows = Array.from(programmeMap.values());

    return (
        <div className="space-y-2">
            {canManage && batch && ['draft', 'returned'].includes(batch.status) && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-800">
                    <LayoutGrid className="h-3.5 w-3.5 shrink-0" />
                    <span><b>Editing enabled.</b> Drag a session to reschedule it; clashes are checked automatically.</span>
                </div>
            )}
            <div className="flex flex-col overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
                <div className="relative max-h-[calc(100vh-235px)] overflow-auto">
                    <table className="w-full min-w-[1300px] border-collapse text-left text-xs relative">
                        <thead className="sticky top-0 z-20 shadow-[0_1px_2px_rgba(0,0,0,0.1)]">
                            {/* Day Group Header */}
                            <tr className="bg-slate-100/90 text-slate-800 font-semibold border-b border-slate-300">
                                <th rowSpan="2" className="sticky left-0 z-30 w-48 border-r border-slate-300 bg-slate-100 p-2.5 text-left align-middle shadow-[1px_0_0_rgba(203,213,225,1)]">
                                    <span className="text-xs font-bold text-slate-900">Programme / structure</span>
                                    <p className="mt-0.5 text-[10px] font-normal text-slate-500">Department programmes</p>
                                </th>
                                {activeDays.map(dayNum => (
                                    <th key={dayNum} colSpan={totalSlots} className="p-2 text-center border-r-2 border-r-slate-400 bg-slate-100 font-bold text-slate-900 text-sm">
                                        {DAYS[dayNum]}
                                    </th>
                                ))}
                            </tr>

                            {/* Clock Hour Slots Sub-Header (7, 8, 9, 10, 11, 12, 13...) */}
                            <tr className="bg-slate-50 text-slate-800 font-bold border-b border-slate-300 text-xs">
                                {activeDays.map(dayNum => (
                                    slotNumbers.map((_, sIdx) => {
                                        const clockHour = startHour + sIdx;
                                        const timeLabel = `${String(clockHour).padStart(2, '0')}:00–${String(clockHour + 1).padStart(2, '0')}:00`;
                                        return (
                                            <th 
                                                key={`${dayNum}-${clockHour}`} 
                                                title={timeLabel}
                                                className={`p-1 text-center border-r border-slate-200 min-w-[32px] font-sans ${sIdx === totalSlots - 1 ? 'border-r-2 border-r-slate-400' : ''}`}
                                            >
                                                <span className="block text-xs font-extrabold text-slate-900">{clockHour}</span>
                                            </th>
                                        );
                                    })
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-300 font-sans">
                            {programmeRows.length === 0 ? (
                                <tr>
                                    <td colSpan={1 + (activeDays.length * totalSlots)} className="p-12 text-center text-slate-500">
                                        <CalendarDays className="mx-auto mb-2 h-7 w-7 text-slate-400" />
                                        No programmes or scheduled sessions found for this department selection.
                                    </td>
                                </tr>
                            ) : (
                                programmeRows.map(prog => (
                                    <tr key={prog.key} className="hover:bg-slate-50/60 transition-colors">
                                        {/* Y-Axis Header: Programme Info */}
                                        <td className="p-2.5 border-r border-slate-300 bg-slate-50/60 align-middle sticky left-0 z-10 shadow-[1px_0_0_rgba(203,213,225,1)]">
                                            <span className="font-bold text-slate-900 block text-xs leading-tight">{prog.name}</span>
                                            {prog.code && <span className="font-mono text-[10px] text-blue-700 font-semibold block">{prog.code}</span>}
                                            <span className="text-[10px] text-slate-500 block">
                                                Level {prog.level} · Module {prog.module}
                                            </span>
                                        </td>

                                        {/* Render Cells per Day */}
                                        {activeDays.map(dayNum => {
                                            const cells = [];
                                            let currentSlotIdx = 0; // 0 to totalSlots - 1

                                            while (currentSlotIdx < totalSlots) {
                                                const slotHour = startHour + currentSlotIdx;
                                                const slotStartMins = slotHour * 60;
                                                const slotEndMins = (slotHour + 1) * 60;

                                                const sessionRow = prog.sessionsList.find(row => {
                                                    if (!row?.session) return false;
                                                    const sDay = Number(row.session?.day_of_week);
                                                    const sStartMins = minutesFromTime(row.session?.starts_at);
                                                    const sEndMins = minutesFromTime(row.session?.ends_at);
                                                    if (sDay !== dayNum) return false;
                                                    if (currentSlotIdx === 0 && sStartMins < slotStartMins && sEndMins > slotStartMins) return true;
                                                    return sStartMins >= slotStartMins && sStartMins < slotEndMins;
                                                });

                                                const isCanDrag = canManage && batch?.id && ['draft', 'returned'].includes(batch?.status);
                                                const cellId = `${prog.key}-${dayNum}-${slotHour}`;

                                                if (sessionRow) {
                                                    const sStartMins = minutesFromTime(sessionRow.session?.starts_at);
                                                    const sEndMins = minutesFromTime(sessionRow.session?.ends_at);
                                                    const effectiveStartMins = Math.max(sStartMins, slotStartMins);
                                                    const durationMins = Math.max(60, sEndMins - effectiveStartMins);
                                                    const durationSlots = Math.max(1, Math.round(durationMins / 60));
                                                    const colSpan = Math.min(durationSlots, totalSlots - currentSlotIdx);
                                                    const isLast = (currentSlotIdx + colSpan) === totalSlots;

                                                    cells.push(
                                                        <td 
                                                            key={`${prog.key}-${dayNum}-slot-${currentSlotIdx}`} 
                                                            colSpan={colSpan}
                                                            className={`p-1 text-center align-middle bg-blue-50/90 border-r border-slate-300 ${isLast ? 'border-r-2 border-r-slate-400' : ''}`}
                                                        >
                                                            <div 
                                                                draggable={isCanDrag}
                                                                onDragStart={(e) => {
                                                                    if (!isCanDrag) return;
                                                                    e.dataTransfer.setData('text/plain', sessionRow.session?.id);
                                                                    window.__draggedSessionRow = sessionRow;
                                                                }}
                                                                className={`group relative p-1.5 rounded bg-white border border-blue-300 shadow-2xs text-[10px] transition-all ${isCanDrag ? 'cursor-grab active:cursor-grabbing hover:border-blue-500 hover:shadow-md' : ''}`}
                                                            >
                                                                <div className="flex items-center justify-between gap-1">
                                                                    <span className="font-mono font-extrabold text-blue-900 leading-tight">
                                                                        {sessionRow.unit?.code || '—'}
                                                                    </span>
                                                                    <div className="flex items-center gap-1">
                                                                        {canManage && (
                                                                            <button 
                                                                                type="button"
                                                                                onClick={(e) => { e.stopPropagation(); onToggleLock?.(sessionRow); }}
                                                                                title={sessionRow.session?.is_locked ? "Unlock session" : "Lock session to prevent auto-changes"}
                                                                                className={`p-0.5 rounded ${sessionRow.session?.is_locked ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'} transition-colors cursor-pointer`}
                                                                            >
                                                                                {sessionRow.session?.is_locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                                                                            </button>
                                                                        )}
                                                                        <span className="text-[8.5px] font-mono font-semibold text-slate-500">
                                                                            {String(sessionRow.session?.starts_at || '').slice(0, 5)}–{String(sessionRow.session?.ends_at || '').slice(0, 5)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="text-[9px] font-semibold text-slate-800 line-clamp-1 mt-0.5" title={sessionRow.unit?.name}>
                                                                    {sessionRow.unit?.name}
                                                                </div>
                                                                <div className="text-[8.5px] text-slate-600 mt-0.5 flex flex-wrap justify-center items-center gap-1 font-mono">
                                                                    {sessionRow.room?.name && <span className="font-semibold text-emerald-800 bg-emerald-50 px-1 rounded border border-emerald-200">📍 {sessionRow.room.name}</span>}
                                                                    {(sessionRow.trainer?.name || sessionRow.trainer?.full_name) && <span className="bg-slate-100 px-1 rounded border border-slate-200">👤 {sessionRow.trainer?.name || sessionRow.trainer?.full_name}</span>}
                                                                </div>

                                                                {canManage && batch?.id === sessionRow.batch?.id && ['draft', 'returned'].includes(batch?.status) && (
                                                                    <div className="mt-1 pt-0.5 border-t border-blue-200 flex items-center justify-center gap-1 opacity-90 hover:opacity-100">
                                                                        <button onClick={() => edit(sessionRow)} className="p-0.5 text-blue-700 hover:bg-blue-100 rounded" title="Edit">
                                                                            <Edit3 className="h-3 w-3" />
                                                                        </button>
                                                                        <button onClick={() => remove(sessionRow.session?.id)} className="p-0.5 text-red-600 hover:bg-red-100 rounded" title="Delete">
                                                                            <Trash2 className="h-3 w-3" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    );

                                                    currentSlotIdx += colSpan;
                                                } else {
                                                    const isLast = (currentSlotIdx + 1) === totalSlots;
                                                    const isHighlighted = dragOverCell === cellId;
                                                    const isRestSlot = isMandatoryRestSlot(prog.sessionsList, dayNum, slotStartMins, slotEndMins);

                                                    cells.push(
                                                        <td 
                                                            key={`${prog.key}-${dayNum}-slot-${currentSlotIdx}`} 
                                                            onDragOver={(e) => {
                                                                if (!isCanDrag) return;
                                                                e.preventDefault();
                                                                e.dataTransfer.dropEffect = 'move';
                                                            }}
                                                            onDragEnter={() => isCanDrag && setDragOverCell(cellId)}
                                                            onDragLeave={() => isCanDrag && setDragOverCell(null)}
                                                            onDrop={(e) => {
                                                                e.preventDefault();
                                                                setDragOverCell(null);
                                                                if (!isCanDrag) return;
                                                                const dragged = window.__draggedSessionRow;
                                                                if (dragged && onSessionMove) {
                                                                    onSessionMove(dragged, dayNum, slotHour);
                                                                }
                                                            }}
                                                            title={isRestSlot ? 'Automatic timetable rest period: 1 hour after six continuous class hours.' : undefined}
                                                            className={`p-1 border-r border-slate-200 text-center align-middle transition-colors ${isHighlighted ? 'bg-blue-200/80 font-bold border-2 border-blue-500' : isRestSlot ? 'bg-amber-50 text-amber-800' : ''} ${isLast ? 'border-r-2 border-r-slate-400' : ''}`}
                                                        >
                                                            {isRestSlot
                                                                ? <span className="block font-semibold text-[8px] leading-tight">1h<br />rest</span>
                                                                : <span className="text-slate-300 font-mono text-[9px]">—</span>}
                                                        </td>
                                                    );
                                                    currentSlotIdx += 1;
                                                }
                                            }

                                            return cells;
                                        })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const TEACHING_REPORT_OPTIONS = [
    { value: 'programme', label: 'Programme / level / module' },
    { value: 'unit', label: 'Unit' },
    { value: 'trainer', label: 'Trainer' },
    { value: 'room', label: 'Room / venue' }
];

const teachingReportMeta = (row, reportView) => {
    const unit = row?.unit || {};
    const programme = unit?.program?.name || unit?.program_name || 'Programme not specified';
    const level = unit?.program_level ?? unit?.program?.level ?? '—';
    const module = unit?.module_in_level ?? unit?.program?.module ?? '—';
    const trainer = row?.trainer?.name || row?.trainer?.full_name || 'Trainer not assigned';
    const room = row?.room?.name || row?.room?.code || 'Room not assigned';
    if (reportView === 'unit') {
        return {
            key: `unit:${unit.id || unit.code || row?.session?.unit_id || row?.session?.id}`,
            label: unit.code || 'Unit not specified',
            subLabel: unit.name || `${programme} · Level ${level} · Module ${module}`,
            detail: `${programme} · Level ${level} · Module ${module}`
        };
    }
    if (reportView === 'trainer') {
        return {
            key: `trainer:${row?.session?.trainer_id || row?.trainer?.id || trainer}`,
            label: trainer,
            subLabel: 'Unit trainer',
            detail: `${unit.code || 'Unit'} · ${programme} · L${level} M${module}`
        };
    }
    if (reportView === 'room') {
        return {
            key: `room:${row?.session?.room_id || row?.room?.id || room}`,
            label: room,
            subLabel: row?.room?.room_type || 'Teaching venue',
            detail: `${unit.code || 'Unit'} · ${programme} · L${level} M${module}`
        };
    }
    return {
        key: `programme:${unit.program?.id || unit.program_id || programme}|${level}|${module}`,
        label: programme,
        subLabel: `Level ${level} · Module ${module}`,
        detail: `${unit.code || 'Unit'} · ${unit.name || ''}`
    };
};

const TeachingTimetableReportGrid = ({ sessions, systemSettings, reportView }) => {
    const configuredDays = systemSettings?.timetable_working_days || [1, 2, 3, 4, 5];
    const activeDays = [...new Set([...configuredDays, ...sessions.map(row => Number(row?.session?.day_of_week))])]
        .filter(day => day >= 1 && day <= 7).sort((a, b) => a - b);
    const startHour = Math.floor(minutesFromTime(systemSettings?.timetable_day_starts_at || '08:00') / 60);
    const endHour = Math.max(startHour + 1, Math.ceil(minutesFromTime(systemSettings?.timetable_day_ends_at || '20:00') / 60));
    const totalSlots = endHour - startHour;
    const rows = React.useMemo(() => {
        const map = new Map();
        sessions.filter(row => row?.session && row?.unit).forEach(row => {
            const meta = teachingReportMeta(row, reportView);
            if (!map.has(meta.key)) map.set(meta.key, { ...meta, sessions: [] });
            map.get(meta.key).sessions.push(row);
        });
        return Array.from(map.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }, [sessions, reportView]);

    const renderCellEntry = (row) => {
        const meta = teachingReportMeta(row, reportView);
        return (
            <div className="rounded border border-blue-200 bg-blue-50/80 px-1.5 py-1 text-center leading-tight">
                <div className="font-mono text-[10px] font-extrabold text-blue-900">{row.unit?.code || '—'}</div>
                <div className="mt-0.5 line-clamp-2 text-[9px] font-semibold text-slate-800">{row.unit?.name || meta.detail}</div>
                <div className="mt-0.5 text-[8px] text-slate-600">
                    {reportView === 'trainer' ? `${meta.detail} · ${row.room?.name || 'Room TBD'}` : reportView === 'room' ? `${meta.detail} · ${row.trainer?.name || row.trainer?.full_name || 'Trainer TBD'}` : meta.detail}
                </div>
            </div>
        );
    };

    return (
        <div className="overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <span className="font-semibold text-slate-900">{TEACHING_REPORT_OPTIONS.find(option => option.value === reportView)?.label}</span>
                <span className="ml-2">{rows.length} {reportView === 'unit' ? 'units' : reportView === 'trainer' ? 'trainers' : reportView === 'room' ? 'venues' : 'programme groups'}</span>
            </div>
            <div className="overflow-auto">
                <table className="w-full min-w-[1300px] border-collapse text-left text-xs">
                    <thead>
                        <tr className="bg-slate-100 text-slate-900">
                            <th className="sticky left-0 z-20 w-56 min-w-[14rem] border-r-2 border-slate-300 bg-slate-100 p-2.5 text-left font-bold">
                                {TEACHING_REPORT_OPTIONS.find(option => option.value === reportView)?.label}
                            </th>
                            {activeDays.map(day => <th key={day} colSpan={totalSlots} className="border-r-2 border-slate-300 p-2 text-center font-bold">{DAYS[day]}</th>)}
                        </tr>
                        <tr className="bg-slate-50 text-slate-700">
                            <th className="sticky left-0 z-20 border-r-2 border-slate-300 bg-slate-50" />
                            {activeDays.flatMap(day => Array.from({ length: totalSlots }, (_, index) => {
                                const hour = startHour + index;
                                return <th key={`${day}-${hour}`} className="min-w-[42px] border-r border-slate-200 p-1 text-center text-[10px] font-semibold">{String(hour).padStart(2, '0')}:00</th>;
                            }))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-300">
                        {!rows.length ? <tr><td colSpan={1 + activeDays.length * totalSlots} className="p-12 text-center text-slate-500">No scheduled sessions found for this report.</td></tr> : rows.map(row => (
                            <tr key={row.key} className="align-top hover:bg-slate-50/60">
                                <th className="sticky left-0 z-10 min-w-[14rem] border-r-2 border-slate-300 bg-white p-2.5 text-left">
                                    <span className="block font-bold text-slate-900">{row.label}</span>
                                    <span className="mt-0.5 block text-[10px] font-normal text-slate-500">{row.subLabel}</span>
                                </th>
                                {activeDays.flatMap(day => {
                                    const dayRows = row.sessions.filter(item => Number(item.session?.day_of_week) === day);
                                    const placed = new Set();
                                    const cells = [];
                                    let index = 0;
                                    while (index < totalSlots) {
                                        const slotStart = (startHour + index) * 60;
                                        const matching = dayRows.filter(item => {
                                            if (placed.has(item.session?.id)) return false;
                                            const start = minutesFromTime(item.session?.starts_at);
                                            const end = minutesFromTime(item.session?.ends_at);
                                            return end > slotStart && start < slotStart + 60;
                                        });
                                        if (matching.length) {
                                            const blockEnd = Math.min((startHour + totalSlots) * 60, Math.max(...matching.map(item => minutesFromTime(item.session?.ends_at) || slotStart + 60)));
                                            const span = Math.max(1, Math.min(totalSlots - index, Math.ceil((blockEnd - slotStart) / 60)));
                                            matching.forEach(item => placed.add(item.session?.id));
                                            cells.push(<td key={`${row.key}-${day}-${index}`} colSpan={span} className="h-16 min-w-[42px] border-r border-slate-200 p-1 align-top">{matching.map(item => <React.Fragment key={item.session?.id}>{renderCellEntry(item)}</React.Fragment>)}</td>);
                                            index += span;
                                        } else {
                                            cells.push(<td key={`${row.key}-${day}-${index}`} className="h-16 min-w-[42px] border-r border-slate-200 p-1 align-top" />);
                                            index += 1;
                                        }
                                    }
                                    return cells;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const DepartmentTimetableManagement = ({ embedded = false, onBack = null, roleBadge = '' }) => {
    const { profile } = useAuth();
    const { institutionSettings } = useTheme();
    const roles = [profile?.primary_role, ...(profile?.secondary_roles || [])].filter(Boolean);
    const canManage = roles.some(role => ['system_administrator', 'chief_principal', 'deputy_principal_academic', 'registrar_academic', 'head_of_department', 'head_of_section'].includes(role));
    const canPublish = roles.some(role => ['system_administrator', 'chief_principal', 'deputy_principal_academic', 'registrar_academic', 'dean_of_faculty'].includes(role));
    const [years, setYears] = React.useState([]), [semesters, setSemesters] = React.useState([]), [departments, setDepartments] = React.useState([]);
    const [yearId, setYearId] = React.useState(''), [semesterId, setSemesterId] = React.useState(''), [departmentId, setDepartmentId] = React.useState('');
    const [sessions, setSessions] = React.useState([]), [batch, setBatch] = React.useState(null), [resources, setResources] = React.useState({ units: [], rooms: [], assignments: [] });
    const [form, setForm] = React.useState(emptySession), [showForm, setShowForm] = React.useState(false), [loading, setLoading] = React.useState(true), [generating, setGenerating] = React.useState(false), [generationResult, setGenerationResult] = React.useState(null), [notice, setNotice] = React.useState(null);
    const [viewMode, setViewMode] = React.useState('grid');
    const [reportView, setReportView] = React.useState('programme');
    const [reportTrainerId, setReportTrainerId] = React.useState('');
    const systemSettings = React.useMemo(() => ({
        timetable_day_starts_at: String(institutionSettings?.timetable_day_starts_at || '07:00').slice(0, 5),
        timetable_day_ends_at: String(institutionSettings?.timetable_day_ends_at || '20:00').slice(0, 5),
        timetable_break_starts_at: String(institutionSettings?.timetable_break_starts_at || '').slice(0, 5),
        timetable_break_ends_at: String(institutionSettings?.timetable_break_ends_at || '').slice(0, 5),
        timetable_working_days: Array.isArray(institutionSettings?.timetable_working_days) && institutionSettings.timetable_working_days.length
            ? institutionSettings.timetable_working_days.map(Number)
            : [1, 2, 3, 4, 5]
    }), [institutionSettings]);
    const [programId, setProgramId] = React.useState('');
    const [levelFilter, setLevelFilter] = React.useState('');
    const [selectedSessionIds, setSelectedSessionIds] = React.useState(new Set());
    const [deleteProgTarget, setDeleteProgTarget] = React.useState('');
    const [showExceptions, setShowExceptions] = React.useState(false);

    const [confirmModal, setConfirmModal] = React.useState({
        isOpen: false,
        title: 'Confirm Action',
        message: '',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        variant: 'danger',
        onConfirm: null
    });

    const triggerConfirm = ({ title, message, confirmText = 'Confirm', variant = 'danger', onConfirm }) => {
        setConfirmModal({
            isOpen: true,
            title: title || 'Confirm Action',
            message: message || 'Are you sure you want to proceed?',
            confirmText,
            cancelText: 'Cancel',
            variant,
            onConfirm: () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                if (onConfirm) onConfirm();
            }
        });
    };

    const toggleSelectAll = () => {
        if (selectedSessionIds.size === visible.length) {
            setSelectedSessionIds(new Set());
        } else {
            setSelectedSessionIds(new Set(visible.map(r => r.session?.id).filter(Boolean)));
        }
    };

    const toggleSelectRow = id => {
        const next = new Set(selectedSessionIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedSessionIds(next);
    };

    const programmesInTimetable = React.useMemo(() => {
        const map = new Map();
        sessions.filter(row => {
            if (departmentId && String(row.department?.id) !== String(departmentId)) return false;
            return true;
        }).forEach(r => {
            if (!r?.unit) return;
            const pId = r.unit?.program?.id || r.unit?.program_id;
            const pName = r.unit?.program?.name || r.unit?.program_name;
            if (pId && pName && !map.has(String(pId))) map.set(String(pId), pName);
        });
        return Array.from(map.entries());
    }, [sessions, departmentId]);

    const handleBulkDelete = (ids, title, confirmMsg) => {
        if (!ids || ids.length === 0) return;
        triggerConfirm({
            title: title || 'Delete Sessions',
            message: confirmMsg || `Are you sure you want to delete ${ids.length} selected session(s)?`,
            confirmText: 'Delete Sessions',
            variant: 'danger',
            onConfirm: async () => {
                setLoading(true);
                try {
                    await Promise.all(ids.map(id => timetableService.deleteSession(id)));
                    setSelectedSessionIds(new Set());
                    await load();
                    setNotice({ type: 'success', message: `Successfully deleted ${ids.length} session(s).` });
                } catch (err) {
                    setNotice({ type: 'error', message: err?.message || 'Failed to delete sessions.' });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleDeleteProgramme = (targetProgId) => {
        const progTuple = programmesInTimetable.find(([id]) => String(id) === String(targetProgId));
        const progName = progTuple ? progTuple[1] : 'selected programme';
        const progSessions = visible.filter(r => r?.unit && String(r.unit?.program?.id || r.unit?.program_id) === String(targetProgId));
        const ids = progSessions.map(r => r.session?.id).filter(Boolean);
        if (!ids.length) return;
        handleBulkDelete(
            ids,
            `Delete ${progName} Sessions`,
            `Are you sure you want to delete all ${ids.length} session(s) for programme '${progName}'?`
        );
        setDeleteProgTarget('');
    };

    React.useEffect(() => { (async () => { try { const [yearRows, departmentRows] = await Promise.all([academicCalendarService.getAcademicYears(), hierarchyService.getDepartments()]); setYears(yearRows || []); setDepartments(departmentRows || []); const active = (yearRows || []).find(row => row.status === 'active') || yearRows?.[0]; if (active) setYearId(active.id); } catch (error) { setNotice({ type: 'error', message: error.message }); } finally { setLoading(false); } })(); }, []);
    React.useEffect(() => { if (!yearId) { setSemesters([]); return; } academicCalendarService.getSemesters(yearId).then(rows => { setSemesters(rows || []); const active = (rows || []).find(row => row.status === 'active') || rows?.[0]; setSemesterId(active?.id || ''); }).catch(error => setNotice({ type: 'error', message: error.message })); }, [yearId]);
    React.useEffect(() => { if (canManage && departments.length === 1 && !departmentId) setDepartmentId(departments[0].id); }, [canManage, departments, departmentId]);

    const load = React.useCallback(async () => { 
        if (!yearId || !semesterId) return; 
        setLoading(true); 
        try { 
            let rows = await timetableService.getSessions(yearId, semesterId); 
            if (departmentId && (canManage || canPublish)) { 
                const batchPromise = timetableService.getBatch(departmentId, yearId, semesterId); 
                const resourcePromise = canManage ? timetableService.getResources(departmentId, yearId, semesterId) : Promise.resolve({ units: [], rooms: [], assignments: [] });
                const [batchRow, resourceRows] = await Promise.all([batchPromise, resourcePromise]); 
                let activeBatch = batchRow;
                const batchIdToUse = batchRow?.id || `local_batch_${departmentId}`;
                const localRecall = localStorage.getItem(`recalled_timetable_${batchIdToUse}`);

                if (canManage && (localRecall === 'draft' || !batchRow || ['draft', 'returned'].includes(batchRow?.status))) {
                    try {
                        const localSessKey = `recalled_sessions_${batchIdToUse}`;
                        const deletedSessKey = `deleted_sessions_${batchIdToUse}`;
                        const localModMap = JSON.parse(localStorage.getItem(localSessKey) || '{}');
                        if (batchRow?.id) {
                            const persistedSessions = await timetableService.getBatchSessions(batchRow.id);
                            (persistedSessions || []).forEach(session => {
                                if (session?.id && !localModMap[session.id]) localModMap[session.id] = session;
                            });
                        }
                        const deletedList = JSON.parse(localStorage.getItem(deletedSessKey) || '[]');
                        const deletedSet = new Set(deletedList);

                        if (localRecall === 'draft' || Object.keys(localModMap).length > 0) {
                            activeBatch = { ...(batchRow || {}), id: batchIdToUse, status: 'draft' };
                            const existingDbSessIds = new Set();

                            rows = rows.filter(row => !deletedSet.has(row.session?.id)).map(row => {
                                if (row.session?.id) existingDbSessIds.add(String(row.session?.id));
                                const mod = localModMap[row.session?.id];
                                const updatedSession = mod ? {
                                    ...row.session,
                                    day_of_week: mod.day_of_week ?? row.session?.day_of_week,
                                    starts_at: mod.starts_at ?? row.session?.starts_at,
                                    ends_at: mod.ends_at ?? row.session?.ends_at,
                                    room_id: mod.room_id ?? row.session?.room_id,
                                    trainer_id: mod.trainer_id ?? row.session?.trainer_id
                                } : row.session;

                                if (String(row.batch?.id) === String(batchIdToUse) || String(row.department?.id) === String(departmentId)) {
                                    return {
                                        ...row,
                                        batch: { ...(row.batch || {}), status: 'draft' },
                                        session: updatedSession
                                    };
                                }
                                return { ...row, session: updatedSession };
                            });

                            Object.entries(localModMap).forEach(([sessId, mod]) => {
                                if (!existingDbSessIds.has(String(sessId)) && !deletedSet.has(String(sessId))) {
                                    try {
                                        const unitObj = resourceRows.units?.find(u => String(u.id) === String(mod.unit_id));
                                        const roomObj = resourceRows.rooms?.find(r => String(r.id) === String(mod.room_id));
                                        const assignmentObj = (resourceRows.assignments || []).find(a => String(a.trainer_id) === String(mod.trainer_id));
                                        const deptObj = departments.find(d => String(d.id) === String(departmentId));

                                        const safeUnit = unitObj
                                            ? unitObj
                                            : {
                                                id: mod.unit_id,
                                                name: 'Pending Unit',
                                                code: '—',
                                                program_level: null,
                                                module_in_level: null,
                                                program: { name: 'Department Programme', code: '' }
                                              };

                                        rows.push({
                                            batch: { id: batchIdToUse, status: 'draft' },
                                            session: {
                                                id: sessId,
                                                unit_id: mod.unit_id,
                                                room_id: mod.room_id,
                                                trainer_id: mod.trainer_id,
                                                day_of_week: Number(mod.day_of_week),
                                                starts_at: String(mod.starts_at || '08:00').slice(0, 5),
                                                ends_at: String(mod.ends_at || '10:00').slice(0, 5)
                                            },
                                            department: deptObj || { id: departmentId, name: '' },
                                            unit: safeUnit,
                                            room: roomObj || { id: mod.room_id, name: 'TBD' },
                                            trainer: assignmentObj?.trainer || { id: mod.trainer_id, full_name: 'TBD', name: 'TBD' }
                                        });
                                    } catch (rowErr) {
                                        console.warn('Failed to reconstruct local session row:', sessId, rowErr);
                                    }
                                }
                            });
                        }
                    } catch (e) {
                        console.warn('Local recall load notice:', e);
                    }
                }
                setSessions(rows || []);
                setBatch(activeBatch); 
                setResources(resourceRows); 
            } else { 
                setSessions(rows || []);
                setBatch(null); 
                setResources({ units: [], rooms: [], assignments: [] }); 
            } 
        } catch (error) { 
            setNotice({ type: 'error', message: error.message }); 
        } finally { 
            setLoading(false); 
        } 
    }, [yearId, semesterId, departmentId, canManage, canPublish, departments]);
    React.useEffect(() => { load(); }, [load]);

    const availablePrograms = React.useMemo(() => {
        const map = new Map();
        sessions.forEach(row => {
            if (departmentId && String(row.department?.id) !== String(departmentId)) return;
            const pId = row.unit?.program?.id || row.unit?.program_id;
            const pName = row.unit?.program?.name || row.unit?.program_name;
            if (pId && pName && !map.has(String(pId))) map.set(String(pId), pName);
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [sessions, departmentId]);

    const availableLevels = React.useMemo(() => {
        const set = new Set();
        sessions.forEach(row => {
            if (departmentId && String(row.department?.id) !== String(departmentId)) return;
            const lvl = row.unit?.program_level ?? row.unit?.program?.level;
            if (lvl !== undefined && lvl !== null && lvl !== '') set.add(String(lvl));
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }, [sessions, departmentId]);

    const visible = React.useMemo(() => sessions.filter(row => {
        if (departmentId && String(row.department?.id) !== String(departmentId)) return false;
        if (programId && String(row.unit?.program?.id || row.unit?.program_id) !== String(programId)) return false;
        if (levelFilter && String(row.unit?.program_level ?? row.unit?.program?.level) !== String(levelFilter)) return false;
        return true;
    }), [sessions, departmentId, programId, levelFilter]);
    const reportTrainers = React.useMemo(() => {
        const map = new Map();
        visible.forEach(row => {
            const trainerId = row?.session?.trainer_id || row?.trainer?.id;
            if (!trainerId) return;
            const trainerName = row?.trainer?.name || row?.trainer?.full_name || 'Trainer not assigned';
            if (!map.has(String(trainerId))) map.set(String(trainerId), trainerName);
        });
        return Array.from(map.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
    }, [visible]);
    const reportVisible = React.useMemo(() => {
        if (reportView !== 'trainer' || !reportTrainerId) return visible;
        return visible.filter(row => String(row?.session?.trainer_id || row?.trainer?.id || '') === String(reportTrainerId));
    }, [visible, reportView, reportTrainerId]);
    React.useEffect(() => {
        if (reportTrainerId && !reportTrainers.some(([id]) => String(id) === String(reportTrainerId))) setReportTrainerId('');
    }, [reportTrainers, reportTrainerId]);
    const selectedUnit = resources.units.find(unit => String(unit.id) === String(form.unit_id));
    const assignments = resources.assignments.filter(item => String(item.unit_id) === String(form.unit_id));
    const requiredEnrollmentForSelectedUnit = Number(
        selectedUnit?.timetable_combined_enrolled_students ?? selectedUnit?.timetable_enrolled_students ?? 0
    ) || 0;
    const compatibleRooms = resources.rooms.filter(room =>
        (!selectedUnit?.required_room_type || room.room_type === selectedUnit.required_room_type) &&
        Number(room?.capacity || 0) >= requiredEnrollmentForSelectedUnit
    );
    const stats = React.useMemo(() => ({ departments: new Set(visible.map(row => row.department?.id)).size, sessions: visible.length, submitted: new Set(visible.filter(row => row.batch?.status === 'submitted').map(row => row.batch?.id)).size, published: new Set(visible.filter(row => row.batch?.status === 'published').map(row => row.batch?.id)).size }), [visible]);

    const createBatch = async () => { const created = await timetableService.createBatch(departmentId, yearId, semesterId); setBatch(created); return created; };
    const autoGenerate = async () => {
        if (!departmentId || !yearId || !semesterId) return;
        const existing = sessions.filter(row => String(row.department?.id) === String(departmentId) && (!batch || String(row.batch?.id) === String(batch?.id)));
        
        const executeGenerate = async () => {
            setGenerating(true);
            setGenerationResult(null);
            try {
                const batchIsLocked = batch?.id && !['draft', 'returned'].includes(batch?.status || '');
                let targetId;

                if (batchIsLocked) {
                    throw new Error('Recall the timetable to draft mode before generating sessions.');
                } else if (batch?.id) {
                    targetId = batch.id;
                } else {
                    // No batch yet — create a persisted departmental batch first.
                    try {
                        const created = await createBatch();
                        targetId = created?.id;
                    } catch (e) { throw e; }
                }

                const result = await timetableService.autoGenerate(targetId, existing.length > 0, departmentId, systemSettings, yearId, semesterId);
                setGenerationResult(result);
                await load();
                const reviewCount = result?.unscheduled?.length || 0;
                const eligible = result?.eligible_units || 0;
                setNotice({ type: reviewCount ? 'warning' : (eligible ? 'success' : 'warning'), message: eligible ? `Generated ${result?.generated_sessions || 0} sessions from ${eligible} eligible units.${reviewCount ? ` ${reviewCount} unit${reviewCount === 1 ? '' : 's'} need review.` : ''}` : 'No eligible units found. Confirm the units belong to this department and each has an approved trainer assignment.' });
            } catch (error) {
                console.error('autoGenerate error:', error);
                setNotice({ type: 'error', message: error?.message || 'Auto-generation failed. Please try again.' });
            } finally {
                setGenerating(false);
            }
        };

        if (existing.length) {
            triggerConfirm({
                title: 'Regenerate Department Timetable',
                message: `Regenerate this department timetable? This will replace ${existing.length} existing session${existing.length === 1 ? '' : 's'}.`,
                confirmText: 'Regenerate Timetable',
                variant: 'warning',
                onConfirm: executeGenerate
            });
        } else {
            await executeGenerate();
        }
    };
    const saveSession = async event => {
        event.preventDefault();
        if (form.ends_at <= form.starts_at) {
            setNotice({ type: 'error', message: 'Session end time must be later than its start time.' });
            return;
        }
        if (!systemSettings.timetable_working_days.includes(Number(form.day_of_week))) {
            setNotice({ type: 'error', message: 'Choose one of the active teaching days configured in System Settings.' });
            return;
        }
        if (form.starts_at < systemSettings.timetable_day_starts_at || form.ends_at > systemSettings.timetable_day_ends_at) {
            setNotice({ type: 'error', message: `Sessions must be within the configured teaching day (${systemSettings.timetable_day_starts_at}–${systemSettings.timetable_day_ends_at}).` });
            return;
        }
        const formStartMins = minutesFromTime(form.starts_at);
        const formEndMins = minutesFromTime(form.ends_at);
        if (violatesTrainerRestRule(sessions, form.trainer_id, form.day_of_week, formStartMins, formEndMins, new Set(form.id ? [form.id] : []))) {
            setNotice({ type: 'error', message: 'A trainer may teach a maximum of six continuous hours. Leave at least a one-hour break before another session.' });
            return;
        }

        try {
            await timetableService.saveSession({ ...form, batch_id: batch.id });
            setShowForm(false);
            setForm(emptySession);
            await load();
            setNotice({ type: 'success', message: 'Teaching session saved.' });
        } catch (error) {
            setNotice({ type: 'error', message: error.message });
        }
    };
    const edit = row => { if (!row?.session) return; setForm({ id: row.session?.id, unit_id: row.session?.unit_id, room_id: row.session?.room_id, trainer_id: row.session?.trainer_id, day_of_week: row.session?.day_of_week, starts_at: String(row.session?.starts_at || '').slice(0, 5), ends_at: String(row.session?.ends_at || '').slice(0, 5) }); setShowForm(true); };
    const remove = id => {
        triggerConfirm({
            title: 'Delete Teaching Session',
            message: 'Are you sure you want to remove this teaching session from the timetable?',
            confirmText: 'Delete Session',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await timetableService.deleteSession(id, batch?.id);
                    await load();
                    setNotice({ type: 'success', message: 'Session removed.' });
                } catch (error) {
                    setNotice({ type: 'error', message: error.message });
                }
            }
        });
    };
    const changeStatus = async (status) => { 
        let reason = null; 
        if (status === 'returned') { 
            reason = window.prompt('Reason for returning this timetable:'); 
            if (reason === null) return; 
        } 
        try { 
            const res = await timetableService.setStatus(batch.id, status, reason); 
            setBatch(prev => ({ ...(prev || {}), ...(res || {}), status: status === 'draft' ? 'draft' : (res?.status || status) })); 
            await load(); 
            setNotice({ type: 'success', message: status === 'draft' ? 'Timetable recalled to draft mode. You can now edit and re-arrange sessions.' : `Timetable ${status}.` }); 
        } catch (error) { 
            setNotice({ type: 'error', message: error.message }); 
        } 
    };
    const reportViewLabel = TEACHING_REPORT_OPTIONS.find(option => option.value === reportView)?.label || 'Programme timetable';
    const selectedReportTrainer = reportTrainers.find(([id]) => String(id) === String(reportTrainerId))?.[1];
    const download = () => timetableService.downloadPdf({ sessions: reportVisible, title: `${departmentId ? departments.find(d => d.id === departmentId)?.name + ' · ' : ''}Teaching Timetable · ${selectedReportTrainer ? `${selectedReportTrainer} · ` : ''}${reportViewLabel}`, period: `${years.find(y => y.id === yearId)?.name || ''} · ${semesters.find(s => s.id === semesterId)?.name || ''}`, settings: institutionSettings, reportView });

    const handleToggleLock = async (sessionRow) => {
        if (!sessionRow?.session) return;
        const newLockedStatus = !sessionRow.session.is_locked;
        try {
            setLoading(true);
            const updated = { ...sessionRow.session, is_locked: newLockedStatus };
            await timetableService.saveSession(updated);
            setNotice({ type: 'success', message: `Session ${newLockedStatus ? 'locked' : 'unlocked'} successfully.` });
            await load();
        } catch (e) {
            setNotice({ type: 'error', message: 'Failed to toggle lock status: ' + e.message });
        } finally {
            setLoading(false);
        }
    };

    const handleMoveSession = async (sessionRow, targetDayNum, targetStartHour) => {
        if (!sessionRow?.session) return false;
        const origStartMins = minutesFromTime(sessionRow.session?.starts_at);
        const origEndMins = minutesFromTime(sessionRow.session?.ends_at);
        const declaredUnitLen = Number(sessionRow.unit?.session_length_minutes);
        const existingSessionLen = origEndMins > origStartMins ? (origEndMins - origStartMins) : 0;
        const durationMins = declaredUnitLen || existingSessionLen || 60;

        const newStartMins = targetStartHour * 60;
        const newEndMins = newStartMins + durationMins;

        const newStartStr = formatTimeFromMinutes(newStartMins);
        const newEndStr = formatTimeFromMinutes(newEndMins);

        // Identify all co-taught / combined delivery sibling sessions
        const groupCode1 = sessionRow.unit?.teaching_group_code || sessionRow.session?.teaching_group_code;
        const combinedSiblings = sessions.filter(other => {
            if (!other?.session) return false;
            if (other.session?.id === sessionRow.session?.id) return true;
            
            const groupCode2 = other.unit?.teaching_group_code || other.session?.teaching_group_code;
            if (groupCode1 && groupCode2 && String(groupCode1).trim() === String(groupCode2).trim()) return true;
            
            const sameSlot = Number(other.session?.day_of_week) === Number(sessionRow.session?.day_of_week) &&
                             String(other.session?.starts_at).slice(0, 5) === String(sessionRow.session?.starts_at).slice(0, 5) &&
                             String(other.session?.ends_at).slice(0, 5) === String(sessionRow.session?.ends_at).slice(0, 5) &&
                             String(other.session?.room_id) === String(sessionRow.session?.room_id) &&
                             String(other.session?.trainer_id) === String(sessionRow.session?.trainer_id);
                             
            if (sameSlot && (sessionRow.unit?.allow_combined_delivery || other.unit?.allow_combined_delivery)) return true;
            return false;
        });

        const siblingIds = new Set(combinedSiblings.map(s => s.session?.id).filter(Boolean));

        // 1. Validate Trainer Availability & Clash (excluding combined group siblings)
        if (sessionRow.session?.trainer_id) {
            // Check local exceptions
            try {
                const exceptions = JSON.parse(localStorage.getItem(`trainerExceptions_${departmentId}`) || '[]');
                const trainerException = exceptions.find(ex => 
                    String(ex.trainer_id).trim() === String(sessionRow.session?.trainer_id).trim() && 
                    Number(ex.day_of_week) === targetDayNum &&
                    newStartMins < minutesFromTime(ex.end_time) && 
                    newEndMins > minutesFromTime(ex.start_time)
                );
                
                if (trainerException) {
                    setNotice({
                        type: 'error',
                        message: `Rejection (Trainer Availability): ${sessionRow.trainer?.name || sessionRow.trainer?.full_name || 'Assigned trainer'} is unavailable on ${DAYS[targetDayNum]} between ${trainerException.start_time} and ${trainerException.end_time} (${trainerException.reason || 'Blocked'}).`
                    });
                    return false;
                }
            } catch (e) {}

            const trainerClash = visible.find(other => {
                if (!other?.session) return false;
                if (siblingIds.has(other.session?.id)) return false;
                if (Number(other.session?.day_of_week) !== targetDayNum) return false;
                if (String(other.session?.trainer_id) !== String(sessionRow.session?.trainer_id)) return false;
                const oStart = minutesFromTime(other.session?.starts_at);
                const oEnd = minutesFromTime(other.session?.ends_at);
                return newStartMins < oEnd && newEndMins > oStart;
            });
            if (trainerClash) {
                setNotice({
                    type: 'error',
                    message: `Rejection (Trainer Clash): ${sessionRow.trainer?.name || 'Assigned trainer'} is already teaching "${trainerClash.unit?.code || trainerClash.unit?.name}" in Room ${trainerClash.room?.name || ''} on ${DAYS[targetDayNum]} between ${String(trainerClash.session?.starts_at || '').slice(0, 5)} and ${String(trainerClash.session?.ends_at || '').slice(0, 5)}.`
                });
                return false;
            }
            if (violatesTrainerRestRule(sessions, sessionRow.session?.trainer_id, targetDayNum, newStartMins, newEndMins, siblingIds)) {
                setNotice({
                    type: 'error',
                    message: `${sessionRow.trainer?.name || 'This trainer'} may teach a maximum of six continuous hours. Leave at least a one-hour break before another session.`
                });
                return false;
            }
        }

        // 2. Validate Room Availability & Clash (excluding combined group siblings)
        if (sessionRow.session?.room_id) {
            const roomClash = visible.find(other => {
                if (!other?.session) return false;
                if (siblingIds.has(other.session?.id)) return false;
                if (Number(other.session?.day_of_week) !== targetDayNum) return false;
                if (String(other.session?.room_id) !== String(sessionRow.session?.room_id)) return false;
                const oStart = minutesFromTime(other.session?.starts_at);
                const oEnd = minutesFromTime(other.session?.ends_at);
                return newStartMins < oEnd && newEndMins > oStart;
            });
            if (roomClash) {
                setNotice({
                    type: 'error',
                    message: `Rejection (Room Clash): Room "${sessionRow.room?.name || 'Selected room'}" is already booked by unit "${roomClash.unit?.code || roomClash.unit?.name}" on ${DAYS[targetDayNum]} between ${String(roomClash.session?.starts_at || '').slice(0, 5)} and ${String(roomClash.session?.ends_at || '').slice(0, 5)}.`
                });
                return false;
            }
        }

        // 3. Validate Programme Structure / Class Schedule Clash (excluding combined group siblings)
        for (const sibling of combinedSiblings) {
            if (!sibling?.unit) continue;
            const targetProgKey = `${sibling.unit?.program?.name || sibling.unit?.program_name} (L${sibling.unit?.program_level ?? sibling.unit?.program?.level} · M${sibling.unit?.module_in_level ?? sibling.unit?.program?.module})`;
            const classClash = visible.find(other => {
                if (!other?.session) return false;
                if (siblingIds.has(other.session?.id)) return false;
                if (Number(other.session?.day_of_week) !== targetDayNum) return false;
                const pKey = `${other.unit?.program?.name || other.unit?.program_name} (L${other.unit?.program_level ?? other.unit?.program?.level} · M${other.unit?.module_in_level ?? other.unit?.program?.module})`;
                if (pKey !== targetProgKey) return false;
                const oStart = minutesFromTime(other.session?.starts_at);
                const oEnd = minutesFromTime(other.session?.ends_at);
                return newStartMins < oEnd && newEndMins > oStart;
            });

            if (classClash) {
                setNotice({
                    type: 'error',
                    message: `Rejection (Class Schedule Clash): Students in ${sibling.unit?.program?.name || 'this programme'} already have unit "${classClash.unit?.code || classClash.unit?.name}" scheduled on ${DAYS[targetDayNum]} between ${String(classClash.session?.starts_at || '').slice(0, 5)} and ${String(classClash.session?.ends_at || '').slice(0, 5)}.`
                });
                return false;
            }
        }

        // Save updated sessions for all combined group units preserving declared unit teaching duration
        try {
            await Promise.all(combinedSiblings.map(s => {
                if (!s?.session) return Promise.resolve();
                const sStartM = minutesFromTime(s.session?.starts_at);
                const sEndM = minutesFromTime(s.session?.ends_at);
                const sDeclared = Number(s.unit?.session_length_minutes);
                const sExisting = sEndM > sStartM ? (sEndM - sStartM) : 0;
                const sDur = sDeclared || sExisting || durationMins;

                const sNewStartMins = targetStartHour * 60;
                const sNewEndMins = sNewStartMins + sDur;
                const sNewStartStr = formatTimeFromMinutes(sNewStartMins);
                const sNewEndStr = formatTimeFromMinutes(sNewEndMins);

                return timetableService.saveSession({
                    id: s.session?.id,
                    unit_id: s.session?.unit_id,
                    room_id: s.session?.room_id,
                    trainer_id: s.session?.trainer_id,
                    day_of_week: targetDayNum,
                    starts_at: sNewStartStr,
                    ends_at: sNewEndStr,
                    batch_id: s.batch?.id || batch?.id
                });
            }));

            await load();
            const groupCodes = Array.from(new Set(combinedSiblings.map(s => s.unit?.code || s.unit?.name))).join(' & ');
            setNotice({
                type: 'success',
                message: combinedSiblings.length > 1
                    ? `Moved combined units (${groupCodes}) to ${DAYS[targetDayNum]} ${newStartStr.slice(0,5)}–${newEndStr.slice(0,5)}.`
                    : `Moved "${sessionRow.unit?.code || 'session'}" to ${DAYS[targetDayNum]} ${newStartStr.slice(0,5)}–${newEndStr.slice(0,5)}.`
            });
            return true;
        } catch (err) {
            setNotice({ type: 'error', message: err?.message || 'Failed to move session.' });
            return false;
        }
    };

    const content = (
        <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    {onBack && (
                        <>
                            <button onClick={onBack} className="shrink-0 text-xs font-semibold text-blue-700 hover:underline">← Staff dashboard</button>
                            <span className="hidden text-slate-300 text-xs sm:inline">/</span>
                        </>
                    )}
                    <h1 className="min-w-0 text-sm font-bold tracking-tight text-slate-900">Department timetable</h1>
                    {roleBadge && (
                        <span className="shrink-0 whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 capitalize">
                            {roleBadge}
                        </span>
                    )}
                    {batch?.status && (
                        <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${badge(batch.status)}`}>
                            {batch.status}
                        </span>
                    )}
                </div>

                <span className="hidden h-px w-full bg-slate-200 lg:block lg:h-5 lg:w-px" />

                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <div className="flex items-center rounded-lg border border-slate-200 bg-slate-100/70 p-0.5">
                        <button
                            type="button"
                            onClick={() => setViewMode('grid')}
                            className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-white text-slate-900 shadow-2xs font-semibold' : 'text-slate-600 hover:text-slate-900'}`}
                            title="Weekly Grid Format"
                        >
                            <LayoutGrid className="h-3.5 w-3.5" />
                            <span>Grid</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode('list')}
                            className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-all cursor-pointer ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-2xs font-semibold' : 'text-slate-600 hover:text-slate-900'}`}
                            title="List Format"
                        >
                            <ListFilter className="h-3.5 w-3.5" />
                            <span>List</span>
                        </button>
                    </div>

                    <select
                        value={reportView}
                        onChange={e => {
                            setReportView(e.target.value);
                            if (e.target.value !== 'trainer') setReportTrainerId('');
                        }}
                        aria-label="Timetable report"
                        title="ASC-style timetable report"
                        className="h-8 w-full rounded-lg border border-blue-200 bg-blue-50 px-2 text-xs font-semibold text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer sm:w-auto sm:max-w-[190px]"
                    >
                        {TEACHING_REPORT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>

                    {reportView === 'trainer' && (
                        <select
                            value={reportTrainerId}
                            onChange={e => setReportTrainerId(e.target.value)}
                            aria-label="Individual trainer timetable"
                            title="Select an individual trainer timetable"
                            className="h-8 w-full rounded-lg border border-emerald-200 bg-emerald-50 px-2 text-xs font-semibold text-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer sm:w-auto sm:max-w-[190px]"
                        >
                            <option value="">All trainers</option>
                            {reportTrainers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                        </select>
                    )}

                    <select
                        value={yearId}
                        onChange={e => setYearId(e.target.value)}
                        aria-label="Academic year"
                        title="Academic year"
                        className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer sm:w-auto sm:max-w-[110px]"
                    >
                        {years.map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
                    </select>

                    <select
                        value={semesterId}
                        onChange={e => setSemesterId(e.target.value)}
                        aria-label="Teaching period"
                        title="Teaching period"
                        className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer sm:w-auto sm:max-w-[130px]"
                    >
                        {semesters.map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
                    </select>

                    {(canManage || canPublish) && (
                        <select
                            value={departmentId}
                            onChange={e => setDepartmentId(e.target.value)}
                            aria-label="Department"
                            title="Department"
                            className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer sm:w-auto sm:max-w-[180px]"
                        >
                            <option value="">All departments</option>
                            {departments.map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
                        </select>
                    )}

                    <select
                        value={programId}
                        onChange={e => setProgramId(e.target.value)}
                        aria-label="Programme"
                        title="Programme"
                        className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer sm:w-auto sm:max-w-[150px]"
                    >
                        <option value="">All programmes</option>
                        {availablePrograms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>

                    <select
                        value={levelFilter}
                        onChange={e => setLevelFilter(e.target.value)}
                        aria-label="Level"
                        title="Level"
                        className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer"
                    >
                        <option value="">All levels</option>
                        {availableLevels.map(lvl => <option key={lvl} value={lvl}>Level {lvl}</option>)}
                    </select>

                    <Button variant="outline" onClick={load} className="h-8 px-2 py-0 text-xs" title="Refresh timetable">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                        <span className="sr-only">Refresh</span>
                    </Button>

                    <Button variant="outline" onClick={download} disabled={!reportVisible.length} className="h-8 px-2.5 py-0 text-xs">
                        <Download className="mr-1 h-3.5 w-3.5" />
                        PDF
                    </Button>

                    {canManage && departmentId && (
                        <Button 
                            onClick={autoGenerate} 
                            disabled={generating} 
                            className="h-8 px-3 py-0 text-xs font-semibold"
                        >
                            <Sparkles className="mr-1 h-3.5 w-3.5" />
                            {generating ? 'Generating…' : visible.length > 0 ? 'Regenerate' : 'Generate'}
                        </Button>
                    )}

                    {canManage && batch && ['draft', 'returned'].includes(batch.status) && (
                        <>
                            <Button variant="outline" onClick={() => setShowExceptions(true)} className="h-8 px-2.5 py-0 text-xs">
                                <CalendarDays className="mr-1 h-3.5 w-3.5" />Availability
                            </Button>
                            <Button variant="outline" onClick={() => {
                                const startMinutes = minutesFromTime(systemSettings.timetable_day_starts_at);
                                const endMinutes = minutesFromTime(systemSettings.timetable_day_ends_at);
                                setForm({
                                    ...emptySession,
                                    day_of_week: systemSettings.timetable_working_days[0] || 1,
                                    starts_at: formatTimeFromMinutes(startMinutes),
                                    ends_at: formatTimeFromMinutes(Math.min(startMinutes + 60, endMinutes))
                                });
                                setShowForm(true);
                            }} className="h-8 px-2.5 py-0 text-xs">
                                <Plus className="mr-1 h-3.5 w-3.5" />Add session
                            </Button>
                        </>
                    )}

                    {canManage && batch && ['draft', 'returned'].includes(batch.status) && (
                        <Button variant="outline" onClick={() => changeStatus('submitted')} className="h-8 px-2.5 py-0 text-xs">
                            <Send className="mr-1 h-3.5 w-3.5" />Submit
                        </Button>
                    )}



                    {canPublish && batch?.status === 'submitted' && (
                        <>
                            <Button onClick={() => changeStatus('published')} className="h-8 px-3 py-0 text-xs">Publish</Button>
                            <Button variant="outline" onClick={() => changeStatus('returned')} className="h-8 px-2.5 py-0 text-xs">Return</Button>
                        </>
                    )}
                </div>
                </div>
            </div>

            <div className="flex min-h-10 flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
                {[['Departments', stats.departments], ['Sessions', stats.sessions], ['Awaiting', stats.submitted], ['Published', stats.published]].map(([label, value]) => (
                    <span key={label} className="whitespace-nowrap text-slate-500">
                        {label} <b className="ml-1 font-mono text-slate-900">{value}</b>
                    </span>
                ))}
                {batch && (
                    <span className="w-full text-slate-500 sm:min-w-[260px] sm:flex-1 sm:truncate">
                        {batch.status === 'draft' && 'Draft timetable — review the grid, then submit it for publication.'}
                        {batch.status === 'submitted' && 'Submitted to Academic Leadership / Registrar for publication.'}
                        {batch.status === 'published' && 'Published timetable — available to students and trainers.'}
                        {batch.status === 'returned' && 'Returned to the department for correction.'}
                    </span>
                )}
                {canManage && batch?.status === 'submitted' && (
                    <button
                        onClick={() => changeStatus('draft')}
                        className="ml-auto inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 font-semibold text-amber-800 hover:bg-amber-100"
                        title="Recall submitted timetable back to draft"
                    >
                        <RotateCcw className="h-3 w-3" /> Recall to draft
                    </button>
                )}
            </div>

            {batch?.return_reason && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"><b>Returned:</b> {batch.return_reason}</div>}
            {generationResult && (
                <details className={`rounded-lg border text-xs ${(generationResult.unscheduled?.length || 0) > 0 ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 font-semibold">
                        <span>
                            Generation result · {generationResult.generated_sessions || 0} of {generationResult.eligible_units || 0} sessions created
                            {(generationResult.unscheduled?.length || 0) > 0 && ` · ${generationResult.unscheduled.length} need review`}
                        </span>
                        <span className="whitespace-nowrap font-normal">View details</span>
                    </summary>
                    {(generationResult.unscheduled?.length || 0) > 0 && (
                        <div className="max-h-44 overflow-y-auto border-t border-amber-200 px-3 py-2">
                            <div className="grid gap-x-6 gap-y-1 md:grid-cols-2">
                                {generationResult.unscheduled.map((item, index) => <p key={`${item.unit_id}-${index}`}><b>{item.unit_code}</b> — {item.reason}</p>)}
                            </div>
                        </div>
                    )}
                </details>
            )}

            {/* Timetable View Format: Grid vs List */}
            {reportView !== 'programme' ? (
                <TeachingTimetableReportGrid sessions={reportVisible} systemSettings={systemSettings} reportView={reportView} />
            ) : viewMode === 'grid' ? (
                <TimetableGridView 
                    sessions={visible} 
                    canManage={canManage} 
                    batch={batch} 
                    edit={edit} 
                    remove={remove} 
                    systemSettings={systemSettings}
                    onSessionMove={handleMoveSession}
                    onToggleLock={handleToggleLock}
                />
            ) : (
                <div className="space-y-3">
                    {/* List View Bulk Actions Bar */}
                    {canManage && batch && ['draft', 'returned'].includes(batch.status) && (
                        <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-slate-100/80 border border-slate-200 rounded-xl text-xs shadow-2xs">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-700">
                                    {selectedSessionIds.size > 0 ? `${selectedSessionIds.size} session(s) selected` : 'Bulk Actions:'}
                                </span>
                                {selectedSessionIds.size > 0 && (
                                    <Button
                                        variant="outline"
                                        onClick={() => handleBulkDelete(Array.from(selectedSessionIds), `Delete ${selectedSessionIds.size} selected session(s)?`)}
                                        className="h-7 text-xs px-2.5 bg-red-50 text-red-700 border-red-200 hover:bg-red-100 font-semibold"
                                    >
                                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                                        Delete Selected ({selectedSessionIds.size})
                                    </Button>
                                )}
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                {/* Delete per Programme */}
                                <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1 shadow-2xs">
                                    <span className="text-slate-500 font-medium text-xs">Delete per programme:</span>
                                    <select
                                        value={deleteProgTarget}
                                        onChange={e => setDeleteProgTarget(e.target.value)}
                                        className="text-xs bg-transparent border-none focus:outline-none font-semibold text-slate-800 cursor-pointer max-w-[180px]"
                                    >
                                        <option value="">Select Programme...</option>
                                        {programmesInTimetable.map(([pId, pName]) => (
                                            <option key={pId} value={pId}>{pName}</option>
                                        ))}
                                    </select>
                                    {deleteProgTarget && (
                                        <button
                                            onClick={() => handleDeleteProgramme(deleteProgTarget)}
                                            className="px-2 py-0.5 bg-red-600 text-white font-bold rounded hover:bg-red-700 transition-all text-[11px] cursor-pointer"
                                        >
                                            Delete Programme Sessions
                                        </button>
                                    )}
                                </div>

                                {/* Delete All */}
                                <Button
                                    variant="outline"
                                    onClick={() => handleBulkDelete(visible.filter(r => !r.session?.is_locked).map(r => r.session?.id).filter(Boolean), `Are you sure you want to DELETE ALL ${visible.filter(r => !r.session?.is_locked).length} UNLOCKED session(s) in this timetable? This action cannot be undone.`)}
                                    disabled={!visible.filter(r => !r.session?.is_locked).length}
                                    className="h-7 text-xs px-2.5 bg-rose-100 text-rose-900 border-rose-300 hover:bg-rose-200 font-bold"
                                >
                                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                                    Delete All ({visible.filter(r => !r.session?.is_locked).length})
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[1050px] text-sm">
                                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 border-b">
                                    <tr>
                                        {canManage && batch && ['draft', 'returned'].includes(batch.status) && (
                                            <th className="px-4 py-3 w-10 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={visible.length > 0 && selectedSessionIds.size === visible.length}
                                                    onChange={toggleSelectAll}
                                                    className="rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                                                    title="Select all"
                                                />
                                            </th>
                                        )}
                                        {['Day / time', 'Unit', 'Programme structure', 'Trainer', 'Room', 'Department', 'Status', 'Actions'].map(label => <th key={label} className="px-4 py-3">{label}</th>)}
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {loading ? (
                                        <tr><td colSpan={canManage ? "9" : "8"} className="p-10 text-center text-slate-500">Loading timetable…</td></tr>
                                    ) : visible.length === 0 ? (
                                        <tr><td colSpan={canManage ? "9" : "8"} className="p-10 text-center text-slate-500"><CalendarDays className="mx-auto mb-2 h-7 w-7" />No teaching sessions available for this selection.</td></tr>
                                    ) : visible.map(row => {
                                        if (!row?.session) return null;
                                        const isSelected = selectedSessionIds.has(row.session?.id);
                                        const canEditRow = canManage && batch?.id === row.batch?.id && ['draft', 'returned'].includes(batch?.status || '');
                                        return (
                                            <tr key={row.session?.id || Math.random()} className={`hover:bg-slate-50 ${isSelected ? 'bg-blue-50/60' : ''}`}>
                                                {canManage && batch && ['draft', 'returned'].includes(batch.status) && (
                                                    <td className="px-4 py-3 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleSelectRow(row.session?.id)}
                                                            className="rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-4 py-3"><b>{DAYS[row.session?.day_of_week]}</b><p className="text-xs text-slate-500">{String(row.session?.starts_at || '').slice(0, 5)}–{String(row.session?.ends_at || '').slice(0, 5)}</p></td>
                                                <td className="px-4 py-3"><b>{row.unit?.code || '—'}</b><p className="max-w-52 text-xs text-slate-500">{row.unit?.name}</p></td>
                                                <td className="px-4 py-3"><p>{row.unit?.program?.name || row.unit?.program_name}</p><p className="text-xs text-slate-500">Level {row.unit?.program_level || row.unit?.program?.level || '—'} · Module {row.unit?.module_in_level || row.unit?.program?.module || '—'}</p></td>
                                                <td className="px-4 py-3">{row.trainer?.name || row.trainer?.full_name || 'Unassigned'}</td>
                                                <td className="px-4 py-3"><b>{row.room?.name || 'TBD'}</b><p className="text-xs text-slate-500">{row.room?.room_type} · {row.room?.capacity}</p></td>
                                                <td className="px-4 py-3">{row.department?.name}</td>
                                                <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs capitalize ${badge(row.batch?.status)}`}>{row.batch?.status}</span></td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-1">
                                                        {canEditRow && (
                                                            <>
                                                                <button onClick={() => edit(row)} className="rounded p-2 hover:bg-blue-50" title="Edit"><Edit3 className="h-4 w-4 text-blue-700" /></button>
                                                                <button onClick={() => remove(row.session?.id)} className="rounded p-2 hover:bg-red-50" title="Delete"><Trash2 className="h-4 w-4 text-red-600" /></button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showForm && <div className="fixed inset-0 z-[100] grid place-items-center overflow-y-auto bg-black/50 p-4"><form onSubmit={saveSession} className="my-auto w-full max-w-2xl rounded-xl bg-white shadow-xl"><div className="border-b p-5"><h2 className="text-xl font-bold">{form.id ? 'Edit' : 'Add'} teaching session</h2><p className="text-sm text-slate-500">Only approved trainer assignments can be scheduled. Manual edits may override automatic rest spacing, while trainer, room, and class clashes remain protected.</p></div><div className="grid gap-4 p-5 md:grid-cols-2"><label className="text-sm font-medium md:col-span-2">Unit<select required value={form.unit_id} onChange={e => { const unitId = e.target.value; const assignment = resources.assignments.find(a => String(a.unit_id) === String(unitId)); setForm(current => ({ ...current, unit_id: unitId, trainer_id: assignment?.trainer_id || '', room_id: '' })); }} className="mt-1 w-full rounded-md border p-2"><option value="">Select unit</option>{resources.units.map(unit => <option key={unit.id} value={unit.id}>{unit.code} — {unit.name} · Level {unit.program_level} Module {unit.module_in_level}</option>)}</select></label><label className="text-sm font-medium">Trainer<select required value={form.trainer_id} onChange={e => setForm(c => ({ ...c, trainer_id: e.target.value }))} className="mt-1 w-full rounded-md border p-2"><option value="">Select approved trainer</option>{assignments.map(a => <option key={a.id} value={a.trainer_id}>{a.trainer?.full_name || a.trainer?.email}</option>)}</select></label><label className="text-sm font-medium">Room<select required value={form.room_id} onChange={e => setForm(c => ({ ...c, room_id: e.target.value }))} className="mt-1 w-full rounded-md border p-2"><option value="">Select compatible room</option>{compatibleRooms.map(room => <option key={room.id} value={room.id}>{room.name} · {room.room_type} · {room.capacity}{room.department_id === departmentId ? ' · Home priority' : ' · External'}</option>)}</select></label><label className="text-sm font-medium">Day<select value={form.day_of_week} onChange={e => setForm(c => ({ ...c, day_of_week: Number(e.target.value) }))} className="mt-1 w-full rounded-md border p-2">{systemSettings.timetable_working_days.map(day => <option key={day} value={day}>{DAYS[day]}</option>)}</select></label><div className="grid grid-cols-1 gap-3 sm:grid-cols-2"><label className="text-sm font-medium">Session starts<input required type="time" min={systemSettings.timetable_day_starts_at} max={systemSettings.timetable_day_ends_at} value={form.starts_at} onChange={e => setForm(c => ({ ...c, starts_at: e.target.value }))} className="mt-1 w-full rounded-md border p-2" /></label><label className="text-sm font-medium">Session ends<input required type="time" min={form.starts_at} max={systemSettings.timetable_day_ends_at} value={form.ends_at} onChange={e => setForm(c => ({ ...c, ends_at: e.target.value }))} className="mt-1 w-full rounded-md border p-2" /></label></div></div><div className="flex flex-col-reverse justify-end gap-2 border-t p-5 sm:flex-row"><Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button><Button type="submit">Save session</Button></div></form></div>}
            
            <TrainerExceptionsModal 
                isOpen={showExceptions} 
                onClose={() => {
                    setShowExceptions(false);
                    load(); // Reload to apply updated exceptions to auto-generate context if needed, though they only affect auto-generation logic when it runs.
                }} 
                departmentId={departmentId} 
                trainers={Array.from(new Map(resources.assignments.map(a => [a.trainer?.id, a.trainer])).values()).filter(t => t?.id)} 
            />
            
            <ActionToast notice={notice} onClose={() => setNotice(null)} />

            {confirmModal.isOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150">
                    <div className="relative w-full max-w-md bg-white rounded-2xl p-6 shadow-2xl border border-slate-100 space-y-4 animate-in zoom-in-95 duration-150">
                        <div className="flex items-start gap-3.5">
                            <div className={`p-3 rounded-full shrink-0 ${confirmModal.variant === 'danger' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                                <AlertTriangle className="h-6 w-6" />
                            </div>
                            <div className="space-y-1">
                                <h3 className="text-lg font-bold text-slate-900 leading-tight">
                                    {confirmModal.title}
                                </h3>
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    {confirmModal.message}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                            <Button
                                variant="outline"
                                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                                className="px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-lg cursor-pointer"
                            >
                                {confirmModal.cancelText}
                            </Button>
                            <Button
                                onClick={confirmModal.onConfirm}
                                className={`px-4 py-2 text-xs font-bold text-white rounded-lg shadow-sm transition-all cursor-pointer ${
                                    confirmModal.variant === 'danger'
                                        ? 'bg-red-600 hover:bg-red-700 shadow-red-200'
                                        : 'bg-amber-600 hover:bg-amber-700 shadow-amber-200'
                                }`}
                            >
                                {confirmModal.confirmText}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
    return content;
};

const TimetableManagement = ({ embedded = false, onBack = null, roleBadge = '' }) => {
    const { profile } = useAuth();
    const roles = [profile?.primary_role, ...(profile?.secondary_roles || [])].filter(Boolean);
    if (roles.includes('student')) return <StudentLayout><MyTimetable audience="student" /></StudentLayout>;
    
    if (embedded) {
        return <DepartmentTimetableManagement embedded={true} onBack={onBack} roleBadge={roleBadge} />;
    }

    return (
        <AdminLayout title="Teaching Timetable">
            <DepartmentTimetableManagement embedded={false} />
        </AdminLayout>
    );
};

export default TimetableManagement;
